import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

export interface NotificationSettings {
  id: string;
  is_test_mode: boolean;
  discord_webhook_url: string | null;
  auto_confirm_enabled: boolean;
  auto_review_enabled: boolean;
  auto_review_delay_minutes: number;
  send_offers_to_all: boolean;
  created_at: string;
  updated_at: string;
}


export const useNotificationSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    try {
      setLoading(true);
      console.log('Fetching notification settings...');
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching notification settings:', error);
        setError(error.message);
        return;
      }

      console.log('Notification settings fetched successfully:', data);
      setSettings({
        ...data,
        auto_confirm_enabled: (data as NotificationSettings).auto_confirm_enabled ?? false,
        auto_review_enabled: (data as NotificationSettings).auto_review_enabled ?? false,
        auto_review_delay_minutes: (data as NotificationSettings).auto_review_delay_minutes ?? 60,
        send_offers_to_all: (data as NotificationSettings).send_offers_to_all ?? false
      });

      setError(null);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient]);

  const updateSettings = useCallback(async (updates: Partial<NotificationSettings>) => {
    if (!settings) return false;

    const supabase = await getSupabaseClient();
    if (!supabase) return false;

    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .update(updates)
        .eq('id', settings.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating notification settings:', error);
        toast({
          title: "Error",
          description: "Failed to update notification settings",
          variant: "destructive",
        });
        return false;
      }

      setSettings({
        ...data,
        auto_confirm_enabled: (data as NotificationSettings).auto_confirm_enabled ?? false,
        send_offers_to_all: (data as NotificationSettings).send_offers_to_all ?? false
      });
      toast({
        title: "Settings Updated",
        description: "Notification settings have been updated successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      return false;
    }
  }, [getSupabaseClient, settings, toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetchSettings: fetchSettings
  };
};